import * as React from 'react';
import { createPortal } from 'react-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  dialogBodyRecipe,
  dialogFooterRecipe,
  dialogRecipe,
  dialogHeaderRecipe,
  overlayBackdropRecipe,
  type DialogHeaderRecipeProps,
  type DialogRecipeProps,
} from '../recipes/overlay.recipe';
import { warnOnUnresponsiveWidth, uiMarkerInternal } from '../recipes/shared';
/**
 * The panel's inset, so a composed DialogHeader and DialogFooter match the
 * body without every call site repeating it. Same idea as CardPaddingContext.
 */
const DialogPaddingContext = React.createContext<'sm' | 'md' | 'lg'>('md');

/**
 * Where a DialogFooter should render when it is not a direct child of
 * DialogContent.
 *
 * The hoisting below matches on DialogContent's own children, which a footer
 * inside a child component can never be — `<DialogContent><NewCallForm/>`
 * hides it one level down, and React cannot see into a component's output
 * before rendering it. Those footers stayed in the scrolling body, indented by
 * the body's padding with their top rule stopping short of the panel edge,
 * which is exactly how the Create Call modal ended up without the rule every
 * other dialog has.
 *
 * A slot fixes it for any depth: the footer renders itself into this node
 * instead. Null while the panel is mounting, and outside a dialog entirely.
 */
const DialogFooterSlotContext = React.createContext<HTMLElement | null>(null);

import { Text } from './Text';
import { Button } from './Button';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export interface DialogContentProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
      'title'
    >,
    DialogRecipeProps {
  /**
   * Dialog heading. Supply this *or* a <DialogTitle> child — Radix warns in
   * development if neither is present, and a dialog without a title is not
   * reachable by screen reader.
   *
   * Passing it here also opts into the standard header layout (title,
   * description, close button on one row). Leaving it off puts the dialog in
   * composed mode, where you lay the header out yourself.
   */
  title?: React.ReactNode;
  /** Optional supporting line under the title. */
  description?: React.ReactNode;
  /** Hide the corner close button, e.g. for a decision the user must make. */
  hideCloseButton?: boolean;
  /** Buttons for the footer. Omit for a dialog with no actions. */
  footer?: React.ReactNode;
  /**
   * Keep the dialog open when the user clicks the backdrop. For a form with
   * unsaved work in it, where a stray click losing everything is worse than
   * the extra click to close deliberately. Escape still closes.
   */
  disableOutsideInteraction?: boolean;
  /**
   * The body scrolls when it outgrows the panel. Set false when the children
   * manage their own scrolling — a sticky footer over a scrolling list, say —
   * so there are not two scrollbars fighting.
   */
  scrollableContent?: boolean;
  padded?: boolean;
  padding?: 'sm' | 'md' | 'lg';
}

/**
 * A modal dialog.
 *
 * Radix handles the hard parts — focus trapping, restoring focus on close,
 * escape and outside-click, and marking the rest of the page inert. This adds
 * the title/description wiring so `aria-labelledby` is never forgotten, and
 * turns the panel into a bottom sheet on phones.
 *
 *   <Dialog>
 *     <DialogTrigger asChild><Button>Edit</Button></DialogTrigger>
 *     <DialogContent title="Edit driver" footer={…}>…</DialogContent>
 *   </Dialog>
 */
export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(function DialogContent(
  {
    className,
    size,
    title,
    description,
    hideCloseButton = false,
    footer,
    disableOutsideInteraction = false,
    scrollableContent = true,
    /**
     * Set false for content that should reach the panel's edges — a photo
     * viewer, a full-bleed list. Before the padding moved off the panel these
     * call sites passed `className="p-0"`, which no longer reaches the body.
     */
    padded = true,
    /** The inset for the whole panel. `md` (16px) by default. */
    padding = 'md',
    children,
    onPointerDownOutside,
    ...props
  },
  ref
) {
  // The body is what scrolls, between a header and footer that stay put.
  const bodyClass = scrollableContent
    ? 'min-h-0 flex-1 overflow-y-auto'
    : 'flex min-h-0 flex-1 flex-col overflow-hidden';
  // No `title` means the caller is laying the header out themselves with
  // <DialogHeader> / <DialogTitle>, so this renders the panel and gets out of
  // the way.
  warnOnUnresponsiveWidth('DialogContent', className);

  const composed = title === undefined;

  /*
   * A composed dialog hands us its header, body and footer as one flat list of
   * children. They used to go into the scroll container together, so the title
   * scrolled away and the header's rule was inset by the panel's padding.
   * Pulling the header and footer out puts each region where it belongs
   * without any of the ~50 call sites changing.
   */
  /*
   * State rather than a ref: a ref alone does not re-render, so a footer
   * portalling into it would have nothing to attach to on the first pass.
   */
  const [footerSlot, setFooterSlot] = React.useState<HTMLDivElement | null>(null);

  const kids = React.Children.toArray(children);
  const header = kids.find(
    (k) => React.isValidElement(k) && k.type === DialogHeader
  );
  const footerChild = kids.find(
    (k) => React.isValidElement(k) && k.type === DialogFooter
  );
  const rest = kids.filter((k) => k !== header && k !== footerChild);

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlayBackdropRecipe()} />
      <DialogPrimitive.Content
        ref={ref}
        // Radix warns when a dialog has no <Description>, unless the content
        // explicitly opts out by passing the aria-describedby prop as
        // undefined. It checks for the key, so it has to be spread in rather
        // than written as aria-describedby={undefined}.
        //
        // Only safe to opt out in props mode, where the absence of the
        // `description` prop really does mean there is no description. In
        // composed mode a <DialogDescription> child may be supplying one, and
        // overriding aria-describedby here would disconnect it.
        {...(composed || description ? {} : { 'aria-describedby': undefined })}
        className={cn(dialogRecipe({ size }), className)}
        onPointerDownOutside={(event) => {
          onPointerDownOutside?.(event);
          if (disableOutsideInteraction) event.preventDefault();
        }}
        {...props}
      >
        <DialogPaddingContext.Provider value={padding}>
         <DialogFooterSlotContext.Provider value={footerSlot}>
          {composed ? (
            <>
              {header
                ? React.cloneElement(
                    header as React.ReactElement<DialogHeaderOwnProps>,
                    { showClose: !hideCloseButton }
                  )
                : null}
              <div
                className={cn(
                  dialogBodyRecipe({ headed: Boolean(header), padding }),
                  !padded && 'p-0',
                  bodyClass
                )}
              >
                {rest}
              </div>
              {footerChild
                ? React.cloneElement(
                    footerChild as React.ReactElement<{ __hoisted?: boolean }>,
                    { __hoisted: true }
                  )
                : null}
              {/* Only when there is no header: a headerless dialog still needs
                a way out, and has nothing to put the button in. */}
              {!hideCloseButton && !header ? (
                <DialogPrimitive.Close asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Close"
                    className="absolute right-4 top-4"
                  >
                    <X />
                  </Button>
                </DialogPrimitive.Close>
              ) : null}
            </>
          ) : (
            <>
              {/* The same component the composed form uses, so the two ways of
                writing a dialog cannot drift apart. */}
              <DialogHeader showClose={!hideCloseButton}>
                <DialogPrimitive.Title asChild>
                  <Text variant="h3">{title}</Text>
                </DialogPrimitive.Title>
                {description ? (
                  <DialogPrimitive.Description asChild>
                    <Text variant="body-sm" tone="muted">
                      {description}
                    </Text>
                  </DialogPrimitive.Description>
                ) : null}
              </DialogHeader>

              {children ? (
                <div
                  className={cn(
                    dialogBodyRecipe({ headed: true, padding }),
                    !padded && 'p-0',
                    bodyClass
                  )}
                >
                  {children}
                </div>
              ) : null}

              {footer ? (
                <div className={dialogFooterRecipe({ padding })}>{footer}</div>
              ) : null}
            </>
          )}
          {/* Where a footer nested inside a child component lands. Empty and
              zero-height until one uses it. */}
          <div className="relay-ui-internal" ref={setFooterSlot} />
         </DialogFooterSlotContext.Provider>
        </DialogPaddingContext.Provider>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

/**
 * COMPOSED HEADER PARTS
 *
 * The other way to build a dialog:
 *
 *   <DialogContent>
 *     <DialogHeader>
 *       <DialogTitle>Release vehicle</DialogTitle>
 *       <DialogDescription>This cannot be undone.</DialogDescription>
 *     </DialogHeader>
 *     …
 *     <DialogFooter><Button>Release</Button></DialogFooter>
 *   </DialogContent>
 *
 * Identical output to the `title` / `description` / `footer` props above. Both
 * exist because ~50 dialogs in the app are already written this way; the props
 * form is shorter and harder to get wrong, so prefer it for new dialogs.
 *
 * DialogTitle wraps Radix's Title, so `aria-labelledby` is wired either way.
 */
export interface DialogHeaderProps
  extends React.HTMLAttributes<HTMLDivElement>,
    DialogHeaderRecipeProps {}

export interface DialogHeaderOwnProps extends DialogHeaderProps {
  /**
   * Draw the close button inside the header row. DialogContent sets this; a
   * call site does not, because whether a dialog is dismissable is the
   * dialog's decision rather than its header's.
   */
  showClose?: boolean;
}

export const DialogHeader = React.forwardRef<
  HTMLDivElement,
  DialogHeaderOwnProps
>(function DialogHeader(
  { className, divided, showClose = false, children, ...props },
  ref
) {
  const padding = React.useContext(DialogPaddingContext);
  return (
    <div
      ref={ref}
      className={cn(dialogHeaderRecipe({ divided, padding }), className)}
      {...props}
    >
      <div className={cn(uiMarkerInternal, 'flex min-w-0 flex-col gap-1')}>
        {children}
      </div>
      {showClose ? (
        <DialogPrimitive.Close asChild>
          {/*
            The row is `items-start` so a two-line title still puts the button
            beside the *first* line. That leaves the 32px button box hanging
            4px below the 24px line it sits next to, which reads as a header
            whose two halves do not line up. Pulling it up by half the
            difference centres it on that first line, with or without a
            description underneath.
          */}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Close"
            className="-mt-1"
          >
            <X />
          </Button>
        </DialogPrimitive.Close>
      ) : null}
    </div>
  );
});

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function DialogTitle({ className, children, ...props }, ref) {
  return (
    <DialogPrimitive.Title ref={ref} asChild {...props}>
      <Text variant="h3" className={className}>
        {children}
      </Text>
    </DialogPrimitive.Title>
  );
});

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function DialogDescription({ className, children, ...props }, ref) {
  return (
    <DialogPrimitive.Description ref={ref} asChild {...props}>
      <Text variant="body-sm" tone="muted" className={className}>
        {children}
      </Text>
    </DialogPrimitive.Description>
  );
});

/**
 * The dialog's action row.
 *
 * Works from anywhere inside a dialog — as a direct child of DialogContent, or
 * nested in a form or a whole child component. The hoisting above only sees
 * DialogContent's own children, so anything deeper renders itself into the
 * footer slot at the bottom of the panel instead. Both routes land in the same
 * place, with the same rule reaching the panel's edges.
 *
 * Being a direct child is still marginally cheaper — it skips the portal's
 * extra render pass — and reads better when the actions are not part of the
 * form. To keep them out of the form while still submitting it, point the
 * button at the form by id:
 *
 *   <form id="edit-driver" …>…fields…</form>
 *   <DialogFooter>
 *     <Button form="edit-driver" type="submit">Save</Button>
 *   </DialogFooter>
 *
 * One caveat the slot does not cover: wrapping a dialog's whole contents in a
 * fragment hides them from the hoisting, because React.Children.toArray counts
 * a fragment as a single child. The footer still finds its way out; a
 * DialogHeader would not.
 */
export interface DialogFooterProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Set by DialogContent on a footer it already hoisted, so that one renders
   * where it stands instead of portalling. Not for call sites.
   * @internal
   */
  __hoisted?: boolean;
}

export const DialogFooter = React.forwardRef<
  HTMLDivElement,
  DialogFooterProps
>(function DialogFooter({ className, __hoisted, ...props }, ref) {
  const padding = React.useContext(DialogPaddingContext);
  const slot = React.useContext(DialogFooterSlotContext);

  const footer = (
    <div
      ref={ref}
      className={cn(dialogFooterRecipe({ padding }), className)}
      {...props}
    />
  );

  /*
   * A footer DialogContent could not see — one inside a child component — puts
   * itself at the bottom of the panel through the slot. Without this it stays
   * in the scrolling body, inset by the body's padding and with its top rule
   * stopping short of the panel edges, which is visibly not the same component
   * as every other dialog's footer.
   *
   * Direct children are already in the right place, so they render as they are
   * and avoid the extra pass a portal costs.
   */
  return !__hoisted && slot ? createPortal(footer, slot) : footer;
});

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Label for the confirming button. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Use the destructive treatment for deletes and cancellations. */
  destructive?: boolean;
  /** Shows a spinner on the confirm button and blocks interaction. */
  loading?: boolean;
  onConfirm: () => void;
}

/**
 * The "are you sure?" dialog, which this product needs constantly — cancelling
 * a call, deleting a driver, voiding an invoice.
 *
 * Exists so those confirmations are phrased and laid out the same way every
 * time, instead of each screen assembling its own.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="sm"
        title={title}
        description={description}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost" disabled={loading}>
                {cancelLabel}
              </Button>
            </DialogClose>
            <Button
              variant={destructive ? 'destructive' : 'primary'}
              loading={loading}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </>
        }
      />
    </Dialog>
  );
}
